/* This example requires Tailwind CSS v2.0+ */
import React, { useContext, useState } from "react";
import Button from "components/UI-lib/Button";

import { OrgContext } from "contexts/OrgContext";
import { UserContext } from "contexts/UserContext";
import { timeout } from "components/utils";
import { UserPlusIcon } from "@heroicons/react/24/solid";
import CopyField from "components/UI-lib/CopyField";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/Graphs/Select";
import "./airplane.scss";

import agent, { type InviteLinkOrganizationAccessLevel } from "server";
import { buildInviteUrl } from "server/utils";
import { useMutation } from "@tanstack/react-query";
import { BREV_SUPPORT_EMAIL } from "constants/index";
import { OrgRoleIds } from "models/Role.model";

interface GenerateInviteLinkProps {
  activeOrgId: string;
}

export function GenerateInviteLink({ activeOrgId }: GenerateInviteLinkProps) {
  const [organizationAccessLevel, setOrganizationAccessLevel] =
    useState<InviteLinkOrganizationAccessLevel>(OrgRoleIds.Member);

  const getInviteToken = async (
    activeOrgId: string,
    accessLevel: InviteLinkOrganizationAccessLevel,
  ) => {
    const res = await agent.Organizations.createInviteLink(
      activeOrgId,
      accessLevel,
    );
    if (res.success && res.data) {
      return res.data;
    } else {
      throw new Error(res.message);
    }
  };

  const {
    data: inviteToken,
    isLoading,
    mutate: generateInviteLink,
    reset: resetInviteLink,
  } = useMutation({
    mutationFn: (accessLevel: InviteLinkOrganizationAccessLevel) =>
      getInviteToken(activeOrgId, accessLevel),
  });

  return (
    <div className="flex flex-col justify-between">
      <div className="grid max-w-md grid-cols-[minmax(0,1fr)_auto] items-end gap-3">
        <div className="space-y-2">
          <label
            className="block text-sm font-medium text-gray-900 dark:text-white"
            htmlFor="invite-link-access-level"
          >
            Organization access level
          </label>
          <Select
            value={organizationAccessLevel}
            disabled={isLoading}
            onValueChange={(value) => {
              setOrganizationAccessLevel(
                value as InviteLinkOrganizationAccessLevel,
              );
              resetInviteLink();
            }}
          >
            <SelectTrigger id="invite-link-access-level">
              <SelectValue>
                {organizationAccessLevel === OrgRoleIds.Member
                  ? "Member"
                  : organizationAccessLevel === OrgRoleIds.Maintainer
                    ? "Maintainer"
                    : "Admin"}
              </SelectValue>
            </SelectTrigger>
            <SelectContent className="w-[min(34rem,calc(100vw-2rem))]">
              <SelectItem value={OrgRoleIds.Member} textValue="Member">
                <span className="block">
                  <span className="block text-sm font-medium text-primary">
                    Member
                  </span>
                  <span className="mt-1 block whitespace-normal text-xs font-normal text-secondary">
                    Create and manage environments, launchables, and networks,
                    and create managed secrets.
                  </span>
                </span>
              </SelectItem>
              <SelectItem value={OrgRoleIds.Maintainer} textValue="Maintainer">
                <span className="block">
                  <span className="block text-sm font-medium text-primary">
                    Maintainer
                  </span>
                  <span className="mt-1 block whitespace-normal text-xs font-normal text-secondary">
                    Includes all Member permissions, plus full management of
                    managed secrets and access permissions.
                  </span>
                </span>
              </SelectItem>
              <SelectItem value={OrgRoleIds.Admin} textValue="Admin">
                <span className="block">
                  <span className="block text-sm font-medium text-primary">
                    Admin
                  </span>
                  <span className="mt-1 block whitespace-normal text-xs font-normal text-secondary">
                    Includes all Maintainer permissions, plus invitations,
                    billing, and organization deletion.
                  </span>
                </span>
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button
          label={isLoading ? "Generating link" : "Generate link"}
          onClick={() => generateInviteLink(organizationAccessLevel)}
          loading={isLoading}
          disabled={!activeOrgId || isLoading}
        />
      </div>
      {inviteToken && inviteToken.length > 0 && (
        <>
          <h4 className="mt-4 text-sm text-gray-900 dark:text-secondary">
            Copy the link and send it to your team to join your organization:
          </h4>
          <h6 className="text-xs text-gray-900 dark:text-secondary">
            (it expires in 7 days)
          </h6>
        </>
      )}
      <div className="mt-3 items-center">
        {inviteToken && inviteToken.length > 0 ? (
          <CopyField
            value={buildInviteUrl(activeOrgId, inviteToken)}
            style={{ width: "90%" }}
            analytics={{
              userid: "unused",
              eventLabel: "unused",
            }}
          />
        ) : null}
      </div>
    </div>
  );
}

const TeamInvite: React.FC = () => {
  const orgContext = useContext(OrgContext);
  const userContext = useContext(UserContext);
  const [loading, setLoading] = useState(false);
  const [inlineAlertMessage, setInlineAlertMessage] = useState("");
  const [inlineAlertSeverity, setInlineAlertSeverity] = useState<
    "error" | "warning" | "info" | "success"
  >("success");
  const [focused, setFocused] = useState(false);
  const [emailList, setEmailList] = useState<string[]>([]);
  const [didSendEmail, setDidSendEmail] = useState(false);
  const [fly, setFly] = useState(false);

  const handleEmailSend = async () => {
    setDidSendEmail(false);
    setFly(false);
    setInlineAlertMessage("");
    setLoading(true);
    const res = await agent.Organizations.inviteByEmail(
      orgContext.activeOrgId,
      emailList,
    );
    if (res.success) {
      setLoading(false);
      setDidSendEmail(true);
      setFly(true);
      setEmailList([]);
      await timeout(2000);
      setFly(false);
    } else {
      setInlineAlertSeverity("error");
      setInlineAlertMessage(
        `Unable to send email invite. Try again or contact support at ${BREV_SUPPORT_EMAIL}.`,
      );
    }
    setLoading(false);
  };

  return (
    <div className="flex w-full flex-col dark:text-secondary">
      <div className="flex flex-row items-center">
        <UserPlusIcon className="w-5 h-5 mr-2" />
        <p className="text-md text-gray-700 dark:text-secondary">
          Invite new members by generating an invite link
        </p>
      </div>
      {orgContext.activeOrg?.isBlocked !== true &&
        userContext.me?.isBlocked !== true && (
          <div className="mt-3">
            <GenerateInviteLink activeOrgId={orgContext.activeOrgId} />
          </div>
        )}
    </div>
  );
};

export default TeamInvite;
